import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Shield,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  User,
  FileText,
  Settings,
  Link2,
  CreditCard,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatCellValue } from '@/lib/utils';
import { PageShell } from '@/components/PageShell';
import { DataTableSkeleton } from '@/components/DataTableControls';

const CATEGORY_ICONS = {
  authentication: User,
  data_access: FileText,
  data_modification: FileText,
  admin_actions: Settings,
  integration: Link2,
  security: AlertTriangle,
  billing: CreditCard,
};

const CATEGORY_COLORS = {
  authentication: 'bg-blue-500/10 text-blue-600 border-blue-200',
  data_access: 'bg-emerald-500/10 text-emerald-600 border-emerald-200',
  data_modification: 'bg-amber-500/10 text-amber-600 border-amber-200',
  admin_actions: 'bg-purple-500/10 text-purple-600 border-purple-200',
  integration: 'bg-cyan-500/10 text-cyan-600 border-cyan-200',
  security: 'bg-red-500/10 text-red-600 border-red-200',
  billing: 'bg-pink-500/10 text-pink-600 border-pink-200',
};

const STATUS_COLORS = {
  success: 'bg-emerald-500/10 text-emerald-600',
  failure: 'bg-red-500/10 text-red-600',
  warning: 'bg-amber-500/10 text-amber-600',
};

const PRODUCT_COLORS = {
  eva_insights: 'bg-slate-500/10 text-slate-700 border-slate-200',
  eva_people: 'bg-purple-500/10 text-purple-700 border-purple-200',
};

export default function AuditLogPage() {
  const { hasRole } = useAuth();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 50;
  
  // Filters
  const [filters, setFilters] = useState({
    category: '',
    action: '',
    user_email: '',
    start_date: '',
    end_date: '',
  });

  const canViewAuditLog = hasRole(['admin', 'approver']);

  useEffect(() => {
    if (canViewAuditLog) {
      fetchCategories();
      fetchSummary();
      fetchLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewAuditLog]);

  useEffect(() => {
    if (canViewAuditLog) {
      fetchLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, canViewAuditLog]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/settings/audit-log/categories');
      setCategories(response.data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await api.get('/settings/audit-log/summary');
      setSummary(response.data);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', limit.toString());
      params.append('offset', (page * limit).toString());
      
      if (filters.category) params.append('category', filters.category);
      if (filters.action) params.append('action', filters.action);
      if (filters.user_email) params.append('user_email', filters.user_email);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const response = await api.get(`/settings/audit-log?${params.toString()}`);
      setLogs(response.data.logs || []);
      setHasMore(response.data.logs?.length === limit);
    } catch (err) {
      console.error('Failed to fetch audit log:', err);
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    setPage(0);
    fetchLogs();
    fetchSummary();
  };

  const handleClearFilters = () => {
    setFilters({
      category: '',
      action: '',
      user_email: '',
      start_date: '',
      end_date: '',
    });
    setPage(0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return formatCellValue(dateStr);
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const formatAction = (action) => {
    return formatCellValue(action?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
  };

  const formatStatusValue = (value) => {
    if (value === null || value === undefined || value === '') return 'None';
    return String(value).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatActionLabel = (log) => {
    const baseLabel = formatAction(log.action);
    const oldStatus = log.details?.old_status;
    const newStatus = log.details?.new_status;
    const groupName = log.details?.group_name;

    if (
      (log.action === 'user_status_changed' || log.action === 'group_status_changed') &&
      (oldStatus !== undefined || newStatus !== undefined)
    ) {
      return `${baseLabel}: ${formatStatusValue(oldStatus)} > ${formatStatusValue(newStatus)}`;
    }

    if ((log.action === 'group_member_added' || log.action === 'group_member_removed') && groupName) {
      return `${baseLabel}: ${groupName}`;
    }

    return baseLabel;
  };

  const formatProduct = (product) => {
    return formatCellValue(product?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));
  };

  const formatResource = (log) => {
    const details = log.details || {};
    const targetLabel = details.target_name && details.target_email
      ? `${details.target_name} (${details.target_email})`
      : details.target_name || details.target_email;

    if (targetLabel) {
      return targetLabel;
    }

    if (details.group_name) {
      return `group: ${details.group_name}`;
    }

    return log.resource_type
      ? `${log.resource_type}${log.resource_id ? `: ${log.resource_id.slice(0, 8)}...` : ''}`
      : formatCellValue(null);
  };

  if (!canViewAuditLog) {
    return (
      <PageShell maxWidth="7xl">
        <Card className="bg-warning/5 border-warning/30">
          <CardContent className="p-6 flex items-center gap-4">
            <AlertTriangle className="h-8 w-8 text-warning" />
            <div>
              <h3 className="font-semibold">Access Denied</h3>
              <p className="text-muted-foreground">Only approvers and administrators can view the audit log.</p>
            </div>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  return (
    <PageShell maxWidth="7xl" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-7 w-7 text-primary" />
            Audit Log
          </h1>
          <p className="text-muted-foreground">Track all activities within your organization</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleFilter}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">Total Events</p>
              <p className="text-2xl font-bold">{summary.total?.toLocaleString() || 0}</p>
            </CardContent>
          </Card>
          {Object.entries(summary.by_category || {}).slice(0, 3).map(([cat, count]) => {
            const Icon = CATEGORY_ICONS[cat] || FileText;
            return (
              <Card key={cat}>
                <CardContent className="p-4 flex items-center gap-3">
                  <Icon className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground capitalize">{cat.replace(/_/g, ' ')}</p>
                    <p className="text-xl font-bold">{count?.toLocaleString() || 0}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={filters.category || "all"} onValueChange={(v) => setFilters(f => ({ ...f, category: v === "all" ? "" : v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.categories?.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>User</Label>
              <Input
                placeholder="user@example.com"
                value={filters.user_email}
                onChange={(e) => setFilters(f => ({ ...f, user_email: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Start Date</Label>
              <Input
                type="datetime-local"
                value={filters.start_date}
                onChange={(e) => setFilters(f => ({ ...f, start_date: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>End Date</Label>
              <Input
                type="datetime-local"
                value={filters.end_date}
                onChange={(e) => setFilters(f => ({ ...f, end_date: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button onClick={handleFilter}>
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
            <Button variant="outline" onClick={handleClearFilters}>
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Log Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4">
              <DataTableSkeleton
                rows={5}
                columns={5}
                showToolbar={false}
                data-testid="audit-log-loading"
              />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Clock className="h-12 w-12 mb-4" />
              <p>No audit log entries found</p>
              <p className="text-sm">Activities will appear here as they occur</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Resource</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>IP Address</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => {
                    const Icon = CATEGORY_ICONS[log.category] || FileText;
                    const product = log.details?.product || log.details?.target_product;
                    return (
                      <TableRow key={log.id}>
                        <TableCell className="whitespace-nowrap text-sm">
                          {formatDate(log.created_at)}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {log.user_email}
                        </TableCell>
                        <TableCell>
                          {product ? (
                            <Badge variant="outline" className={PRODUCT_COLORS[product] || ''}>
                              {formatProduct(product)}
                            </Badge>
                          ) : (
                            <span className="text-sm text-muted-foreground">{formatCellValue(null)}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={CATEGORY_COLORS[log.category] || ''}>
                            <Icon className="h-3 w-3 mr-1" />
                            {log.category?.replace(/_/g, ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatActionLabel(log)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatResource(log)}
                        </TableCell>
                        <TableCell>
                          <Badge className={STATUS_COLORS[log.status] || STATUS_COLORS.success}>
                            {log.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {formatCellValue(log.ip_address)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          
          {/* Pagination */}
          {logs.length > 0 && (
            <div className="flex items-center justify-between p-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {page * limit + 1} - {page * limit + logs.length} entries
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasMore}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </PageShell>
  );
}
